import type { QueryKey } from "@tanstack/react-query";
import type { DocumentFormValues } from "../schema/document-schema";
import type { CustomTool } from "../schema/tool-schema";
import {
  type CustomToolPayload,
  deleteToolRemote,
  duplicateToolRemote,
  fetchTools,
  putTool,
  type WritableScope,
} from "./custom-tool-api";
import type { GeneratedDocument } from "./document-generator";
import {
  type DocumentPayload,
  deleteDocumentRemote,
  fetchDocument,
  fetchDocuments,
  putDocument,
} from "./documents-api";
import type { RiskAssessment } from "./generate-api";
import { getBrowserQueryClient } from "./query-client";

/**
 * Persistence for generated documents (drafts + finals) and user-built custom
 * tools.
 *
 * **Documents** are org-owned and team-shared (`/api/documents`). React Query is
 * the cache and the server is the source of truth: reads come synchronously from
 * the query cache ({@link getDocument}/{@link listDocuments}), writes update the
 * cache immediately via `setQueryData` AND mirror to the server in the
 * background, and {@link hydrateDocuments} (or any `useDocuments` mount) fetches
 * the workspace's documents into the cache. Because the synchronous helpers and
 * the `useQuery` readers share one client instance, an imperative write
 * re-renders every open list with no refresh — the role the old localStorage
 * cache + `documents:updated` event used to play.
 *
 * **Custom tools (templates)** are server-backed too (`/api/templates`), on the
 * same cache-plus-mirror model: reads come from the React Query cache, writes
 * update it immediately and mirror to the server in the background, and a
 * one-time {@link migrateLegacyTools} pushes any tools left in the old
 * localStorage cache up to the server. A template's `scope` selects its library
 * — "personal" (private to the user) or "workspace" (shared with the org).
 */

/**
 * A document's lifecycle status. `generating`/`generation_failed` are the
 * transient states of the async single-document generation flow: the row is
 * created `generating` on Generate, then the worker settles it to `draft` or
 * `generation_failed`. See `document-generation-progress.tsx`.
 */
export type DocumentStatus = "generating" | "generation_failed" | "draft" | "final" | "signed";

/**
 * Whether a document came from a built-in type, a user-built custom tool, or was
 * written from scratch ("blank" — see `lib/blank-document.ts`).
 */
export type DocumentKind = "builtin" | "custom" | "blank";

/**
 * One signature on a document. Signed in-app (signatureData = base64 PNG held in
 * the browser) or pending an external signer. `role` labels the block (e.g.
 * "Signatory", "Employee").
 */
export type SignatureMethod = "invite" | "offline";

export type SignatureBlock = {
  id: string;
  role: string;
  signerName: string;
  signerEmail?: string;
  status: "pending" | "signed";
  /** The letter's signatory — this block fills the sign-off line above the name. */
  primary?: boolean;
  /**
   * How a pending block gets signed: `invite` — emailed a link via "Send for
   * signature"; `offline` — signed by hand on the printed copy (never e-signed).
   * Absent means signed in-app.
   */
  method?: SignatureMethod;
  /**
   * Who was picked for this block, when it isn't a typed-in name: an employee,
   * or a workspace member (their user id). Cleared when the name is edited.
   */
  employeeId?: string;
  userId?: string;
  /** base64 PNG data URL of the captured signature, once signed. */
  signatureData?: string;
  signedName?: string;
  signedAt?: string;
};

export type StoredDocument = {
  id: string;
  kind: DocumentKind;
  /** Built-in type id, or the custom tool id. */
  documentType: string;
  /** Snapshotted display label (the type/tool name). */
  typeName: string;
  /** Human label shown in the library; defaults to the type name. */
  name: string;
  status: DocumentStatus;
  /** Built-in form values, or the custom tool's field-key → value map. */
  values: DocumentFormValues | Record<string, string>;
  /** Last generated output; null until the user generates. */
  output: GeneratedDocument | null;
  /** In-app signature blocks (added/signed on the review step). */
  signatures?: SignatureBlock[];
  /** Backend signing-request id once sent out for external signature. */
  signingRequestId?: string;
  /** Last risk-scan result, if one has been run on this document. */
  risk?: RiskAssessment;
  /** Employee the document is about, when picked from the roster. */
  employeeId?: string;
  /** The incident this document was generated for, when started from one. */
  incidentId?: string;
  /** The letterhead template chosen for this document (restores the picker). */
  letterheadId?: string;
  /**
   * When the reminder picker was last offered for this document (ISO), or null
   * if it never has been. Server-owned — only `POST /:id/reminders` writes it,
   * and it's stripped from the upsert payload — so the client only reads it, to
   * know the document has already been asked about reminders once.
   */
  remindersPromptedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

/** Legacy localStorage key — read once for the one-time server migration. */
const LEGACY_DOCUMENTS_KEY = "hrdocket.documents.v1";
const TOOLS_KEY = "hrdocket.tools.v1";

/** The React Query key the document cache lives under. */
export const DOCUMENTS_QUERY_KEY: QueryKey = ["documents"];
/**
 * Prefix key for the server-paginated library views (`useDocumentsPage`). Kept
 * separate from the full-list cache above: that cache is the synchronous source
 * of truth for the wizard, while these are throwaway server slices that just
 * need re-fetching whenever a write changes the underlying set.
 */
export const DOCUMENTS_PAGE_QUERY_KEY: QueryKey = ["documents", "page"];
/** The React Query key the custom-tool cache lives under. */
export const TOOLS_QUERY_KEY: QueryKey = ["custom-tools"];

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

/** Read a legacy localStorage array (used only by the one-time migrations). */
function read<T>(key: string): T[] {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function newId(): string {
  return isBrowser() && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ── Documents (React Query cache + server mirror) ────────────────────────────
// The query cache is the synchronous source of truth; the server is the shared
// source of truth. Writes mirror to the server best-effort (failures stay in the
// cache and get re-pushed on the next fetch).

/** The server-bound payload for a record (drops id/timestamps). */
function toPayload(doc: StoredDocument): DocumentPayload {
  // `remindersPromptedAt` is server-owned (only the reminders endpoint writes
  // it), so it's dropped here rather than echoed back on every save.
  const { id: _id, createdAt: _c, updatedAt: _u, remindersPromptedAt: _r, ...payload } = doc;
  return payload;
}

/** Mirror an upsert to the server; swallow errors (kept in cache, retried later). */
function syncPut(doc: StoredDocument): void {
  if (!isBrowser()) return;
  void putDocument(doc.id, toPayload(doc)).catch(() => {});
}

/** Mirror a delete to the server; swallow errors. */
function syncDelete(id: string): void {
  if (!isBrowser()) return;
  void deleteDocumentRemote(id).catch(() => {});
}

/** Read the current document cache (empty off the browser or before first fetch). */
function getDocsCache(): StoredDocument[] {
  return getBrowserQueryClient()?.getQueryData<StoredDocument[]>(DOCUMENTS_QUERY_KEY) ?? [];
}

/** Replace the document cache, re-rendering every `useDocuments` subscriber. */
function setDocsCache(next: StoredDocument[]): void {
  getBrowserQueryClient()?.setQueryData(DOCUMENTS_QUERY_KEY, next);
}

/**
 * Mark the server-paginated library slices stale so any mounted list refetches.
 * Cheap when no library is open (inactive queries refetch on next mount), and
 * the bridge that keeps the paginated views in step with full-list writes.
 */
function invalidateDocumentPages(): void {
  void getBrowserQueryClient()?.invalidateQueries({
    queryKey: DOCUMENTS_PAGE_QUERY_KEY,
  });
}

let legacyMigrated = false;

/**
 * One-time migration of documents left in localStorage by the previous
 * (localStorage-cache) implementation: push each up to the server, then drop the
 * key. Only clears the key once every record is safely persisted, so an offline
 * load retries on the next visit.
 */
async function migrateLegacyDocuments(): Promise<void> {
  if (legacyMigrated || !isBrowser()) return;
  legacyMigrated = true;
  const local = read<StoredDocument>(LEGACY_DOCUMENTS_KEY);
  if (local.length === 0) {
    window.localStorage.removeItem(LEGACY_DOCUMENTS_KEY);
    return;
  }
  try {
    await Promise.all(local.map((d) => putDocument(d.id, toPayload(d))));
    window.localStorage.removeItem(LEGACY_DOCUMENTS_KEY);
  } catch {
    // Offline / unauthenticated — leave the key and let a later load retry.
    legacyMigrated = false;
  }
}

/**
 * The `queryFn` behind `useDocuments` and {@link hydrateDocuments}: migrate any
 * legacy localStorage drafts once, then fetch the workspace's documents.
 */
export async function documentsQueryFn(): Promise<StoredDocument[]> {
  await migrateLegacyDocuments();
  return fetchDocuments();
}

/**
 * Ensures the document cache is populated (fetches once if empty). Safe to call
 * repeatedly; React Query dedupes in-flight fetches and returns cached data.
 */
export async function hydrateDocuments(): Promise<void> {
  const qc = getBrowserQueryClient();
  if (!qc) return;
  await qc
    .ensureQueryData({
      queryKey: DOCUMENTS_QUERY_KEY,
      queryFn: documentsQueryFn,
    })
    .catch(() => {
      // Offline / unauthenticated — readers keep working from whatever's cached.
    });
}

/** All saved documents, newest-updated first. */
export function listDocuments(): StoredDocument[] {
  return [...getDocsCache()].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getDocument(id: string): StoredDocument | null {
  return getDocsCache().find((d) => d.id === id) ?? null;
}

/**
 * The full document for an id, fetched and cached when it isn't already held.
 *
 * The library lists documents through the paginated endpoint, which sends rows
 * without their content — so an action reached from a row (rename, duplicate)
 * cannot assume the whole document is in the cache and asks for it here.
 * Returns `null` when it cannot be loaded, which callers surface rather than
 * writing a half-populated record back.
 */
export async function loadDocument(id: string): Promise<StoredDocument | null> {
  const cached = getDocument(id);
  if (cached) return cached;
  try {
    const doc = await fetchDocument(id);
    const all = getDocsCache();
    setDocsCache(all.some((d) => d.id === doc.id) ? all : [...all, doc]);
    return doc;
  } catch (err) {
    console.error("[documents] could not load document", err);
    return null;
  }
}

/** Renames a document, loading its content first so the upsert keeps it. */
export async function renameDocument(id: string, name: string): Promise<boolean> {
  const doc = await loadDocument(id);
  if (!doc) return false;
  upsertDocument({ ...doc, name });
  return true;
}

/**
 * Inserts or updates a document. Pass an existing `id` to update in place; omit
 * it to create. Returns the persisted record (with its id and timestamps).
 */
export function upsertDocument(
  input: Partial<StoredDocument> & {
    documentType: string;
    typeName: string;
    values: DocumentFormValues | Record<string, string>;
  },
): StoredDocument {
  const now = new Date().toISOString();
  const all = getDocsCache();
  const existing = input.id ? all.find((d) => d.id === input.id) : undefined;

  const record: StoredDocument = {
    id: existing?.id ?? input.id ?? newId(),
    kind: input.kind ?? existing?.kind ?? "builtin",
    documentType: input.documentType,
    typeName: input.typeName,
    name: input.name ?? existing?.name ?? "",
    status: input.status ?? existing?.status ?? "draft",
    values: input.values,
    output: input.output ?? existing?.output ?? null,
    signatures: input.signatures ?? existing?.signatures,
    // Passed as undefined, it clears: a closed request is let go of so the
    // document can be sent for signature again.
    signingRequestId:
      "signingRequestId" in input ? input.signingRequestId : existing?.signingRequestId,
    risk: input.risk ?? existing?.risk,
    employeeId: input.employeeId ?? existing?.employeeId,
    incidentId: input.incidentId ?? existing?.incidentId,
    letterheadId: input.letterheadId ?? existing?.letterheadId,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };

  const next = existing ? all.map((d) => (d.id === record.id ? record : d)) : [...all, record];
  setDocsCache(next);
  invalidateDocumentPages();
  syncPut(record);
  return record;
}

/**
 * Pushes a cached document to the server and awaits it. Use before a follow-up
 * server call that needs the row to already exist (e.g. attaching reminders),
 * since {@link upsertDocument}'s normal mirror is fire-and-forget.
 */
export async function flushDocument(id: string): Promise<void> {
  if (!isBrowser()) return;
  const doc = getDocument(id);
  if (!doc) return;
  await putDocument(id, toPayload(doc));
}

export function deleteDocument(id: string): void {
  setDocsCache(getDocsCache().filter((d) => d.id !== id));
  invalidateDocumentPages();
  syncDelete(id);
}

/**
 * Clones a document into a new draft record ("Copy of …") and returns it, so the
 * caller can open it for editing. Returns null if the source can't be loaded.
 */
export async function duplicateDocument(id: string): Promise<StoredDocument | null> {
  const source = await loadDocument(id);
  if (!source) return null;
  return upsertDocument({
    kind: source.kind,
    documentType: source.documentType,
    typeName: source.typeName,
    values: source.values,
    output: source.output,
    name: `Copy of ${source.name || source.typeName}`,
    status: "draft",
    employeeId: source.employeeId,
  });
}

// ── Custom tools (React Query cache + server mirror) ─────────────────────────
// Same model as documents: the query cache is the synchronous source of truth,
// the server is the shared source of truth. Writes update the cache immediately
// and mirror to the server in the background.

/** The server payload for a tool (drops id/timestamps). */
function toToolPayload(tool: CustomTool): CustomToolPayload {
  const { id: _id, createdAt: _c, updatedAt: _u, ...payload } = tool;
  return payload;
}

function getToolsCache(): CustomTool[] {
  return getBrowserQueryClient()?.getQueryData<CustomTool[]>(TOOLS_QUERY_KEY) ?? [];
}

function setToolsCache(next: CustomTool[]): void {
  getBrowserQueryClient()?.setQueryData(TOOLS_QUERY_KEY, next);
}

let toolsLegacyMigrated = false;

/**
 * One-time migration of custom tools left in localStorage by the previous
 * (local-only) implementation: push each up to the server keeping its id, then
 * drop the key. Only clears the key once every tool is safely persisted.
 */
async function migrateLegacyTools(): Promise<void> {
  if (toolsLegacyMigrated || !isBrowser()) return;
  toolsLegacyMigrated = true;
  const local = read<CustomTool>(TOOLS_KEY);
  if (local.length === 0) {
    window.localStorage.removeItem(TOOLS_KEY);
    return;
  }
  try {
    await Promise.all(local.map((t) => putTool(t.id, toToolPayload(t))));
    window.localStorage.removeItem(TOOLS_KEY);
  } catch {
    toolsLegacyMigrated = false;
  }
}

/** The `queryFn` behind `useTools`: migrate legacy tools once, then fetch. */
export async function toolsQueryFn(): Promise<CustomTool[]> {
  await migrateLegacyTools();
  return fetchTools();
}

/** Ensures the tool cache is populated (fetches once if empty). */
export async function hydrateTools(): Promise<void> {
  const qc = getBrowserQueryClient();
  if (!qc) return;
  await qc.ensureQueryData({ queryKey: TOOLS_QUERY_KEY, queryFn: toolsQueryFn }).catch(() => {});
}

/** All custom tools, newest-updated first. */
export function listTools(): CustomTool[] {
  return [...getToolsCache()].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getTool(id: string): CustomTool | null {
  return getToolsCache().find((t) => t.id === id) ?? null;
}

/** Inserts or updates a custom tool; returns the persisted record. */
export function upsertTool(
  input: Omit<CustomTool, "id" | "createdAt" | "updatedAt"> & {
    id?: string;
  },
): CustomTool {
  const now = new Date().toISOString();
  const all = getToolsCache();
  const existing = input.id ? all.find((t) => t.id === input.id) : undefined;

  const record: CustomTool = {
    id: existing?.id ?? input.id ?? newId(),
    name: input.name,
    description: input.description,
    titleTemplate: input.titleTemplate,
    body: input.body,
    fields: input.fields,
    // New tools default to a private (personal) template; editing keeps the
    // existing scope. The server ignores `scope` on an in-place update, so a
    // saved workspace template stays shared.
    scope: input.scope ?? existing?.scope ?? "personal",
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };

  const next = existing ? all.map((t) => (t.id === record.id ? record : t)) : [...all, record];
  setToolsCache(next);
  if (isBrowser()) void putTool(record.id, toToolPayload(record)).catch(() => {});
  return record;
}

/**
 * Duplicates a template into `scope` (Duplicate / Share to workspace / Copy to
 * personal). The server creates the copy with a fresh id; we mirror the returned
 * tool straight into the cache so the list updates without a refetch.
 */
export async function duplicateTool(id: string, scope: WritableScope): Promise<CustomTool> {
  const created = await duplicateToolRemote(id, scope);
  setToolsCache([...getToolsCache(), created]);
  return created;
}

export function deleteTool(id: string): void {
  setToolsCache(getToolsCache().filter((t) => t.id !== id));
  if (isBrowser()) void deleteToolRemote(id).catch(() => {});
}
